import { useEffect } from 'react'
import type { ToastT } from 'sonner'

import { NOTIFICATIONS_SOCKET_NAME } from '../socketConfigs'
import { reconnectSocket, socketOn } from '@features/websocket/socketService'
import { emitWithToast } from '@lib/notifications'
import type { NotificationType } from '@lib/notifications'
import { getResourceUrl } from '@lib/resourceUrl'
import { useAppSelector } from '@store/hooks'

const LEVEL_TO_TOAST_TYPE: Record<string, ToastT['type']> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
}

type NotificationPayload = {
  title?: string
  message?: string
  level?: string
  resource_type?: string | null
  resource_id?: string | number | null
}

/**
 * Generate a deterministic notification ID from WS event content.
 * Same event arriving in multiple tabs produces the same ID,
 * so IDB `put` replaces rather than creating duplicates.
 */
function buildWsNotificationId(notification: NotificationPayload): string {
  const parts = [
    'ws',
    notification.level ?? '',
    notification.title ?? '',
    notification.message ?? '',
    notification.resource_type ?? '',
    String(notification.resource_id ?? ''),
  ]

  return parts.join('-').replace(/\s+/g, '-').toLowerCase().slice(0, 128)
}

export function buildToastFromNotification(notification: NotificationPayload | null | undefined) {
  if (!notification?.title && !notification?.message) {
    console.error('[NotificationSocket] Received notification without title or message:', notification)

    return null
  }

  const { title, message, level, resource_type, resource_id } = notification

  if (!title || !message || !level) {
    console.error('[NotificationSocket] Notification has missing fields:', { title, message, level })
  }

  const toastMessage = [title, message].filter(Boolean).join(': ')
  const type = LEVEL_TO_TOAST_TYPE[level ?? ''] ?? 'info'
  const duration = level === 'error' ? 5000 : 3000

  const resourceUrl = getResourceUrl(resource_type, resource_id)
  const action = resourceUrl ? { label: 'View', onClick: () => window.open(resourceUrl, '_blank') } : undefined

  return {
    message: toastMessage,
    type,
    duration: action ? 15000 : duration,
    closeButton: !!action,
    action,
  }
}

export function useNotificationSocket() {
  const sessionUserId = useAppSelector((s) => s.session.user?.id)

  // Reconnect notifications socket when session user becomes available
  useEffect(() => {
    if (sessionUserId) {
      reconnectSocket(NOTIFICATIONS_SOCKET_NAME)
    }
  }, [sessionUserId])

  useEffect(() => {
    return socketOn(NOTIFICATIONS_SOCKET_NAME, '*', (data) => {
      const notification = data?.data as NotificationPayload | undefined
      const toastConfig = buildToastFromNotification(notification)
      if (!toastConfig || !notification) return

      const resourceUrl = getResourceUrl(notification.resource_type, notification.resource_id)
      const bellType: NotificationType = ['success', 'error', 'warning', 'info'].includes(notification.level ?? '')
        ? (notification.level as NotificationType)
        : 'info'

      emitWithToast(
        {
          id: buildWsNotificationId(notification),
          type: bellType,
          title: notification.title ?? '',
          message: notification.message,
          persistent: false,
          actions: resourceUrl
            ? [{ label: 'View', actionType: 'open-url', icon: 'external', payload: { url: resourceUrl } }]
            : undefined,
        },
        {
          ...toastConfig,
          duration: toastConfig.action ? 15000 : toastConfig.duration,
        },
      )
    })
  }, [])
}
